// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef, useState } from 'react';

import type { PartCatalog, PartCategoryId } from '@evolvemep/api-ts';
import { ComboboxChevron, ComboboxClearButton, Input, InputBase, Loader, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { CatalogCategoryNav } from 'modules/Shop/CatalogSetup/CatalogCategoryNav';
import { type CategoryPath, useCategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type Props = {
  partCatalog: PartCatalog | null | undefined;
  value?: CategoryPath[number] | null;
  onChange: (category: CategoryPath[number] | null) => void;
  label?: string;
  loading?: boolean;
  disableCategories?: PartCategoryId[];
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  placeholder?: string;
  w?: number | `${number}%`;
};

const CategoryFields = ({
  partCatalog,
  value,
  disableCategories,
  onChange,
}: Pick<Props, 'value' | 'disableCategories' | 'onChange'> & { partCatalog: PartCatalog }) => {
  const [internalPath, onCategorySelected] = useCategoryPath(value);
  const form = useForm<{ category: CategoryPath[number] | null }>({
    initialValues: { category: value ?? null },
    validate: {
      category: (v) => isNil(v) || v.partCategoryId === value?.partCategoryId,
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    // Cast is safe due to form.validate
    onChange(values.category as CategoryPath[number]);
  };

  useFormPopover(form, onSubmit);

  return (
    <Stack mih={100} gap={4} mah={250} style={{ overflow: 'hidden' }}>
      <CatalogCategoryNav
        categoryPath={internalPath}
        onCategorySelected={(category) => {
          onCategorySelected(category);
          form.setFieldValue('category', category);
        }}
        partCatalog={partCatalog}
        disableCategories={disableCategories}
      />
    </Stack>
  );
};

export const ExistingCategorySelector = ({
  partCatalog,
  onChange,
  value,
  disabled,
  required,
  clearable,
  w,
  placeholder = 'Select a category...',
  label = 'Category',
  disableCategories,
  loading,
}: Props) => {
  const [opened, setOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <FormPopover
      opened={opened}
      onClose={() => setOpened(false)}
      styles={{
        dropdown: {
          width: buttonRef.current?.clientWidth,
        },
      }}
      withCloseButton={false}
      confirmationText="Select"
      target={
        <InputBase
          ref={buttonRef}
          label={label}
          required={required}
          rightSection={
            loading ? (
              <Loader size="sm" />
            ) : !clearable || isNil(value) ? (
              <ComboboxChevron onClick={() => setOpened(true)} />
            ) : (
              <ComboboxClearButton onClear={() => onChange(null)} />
            )
          }
          disabled={disabled || isNil(partCatalog)}
          type="button"
          component="button"
          onClick={() => setOpened(true)}
          w={w}
        >
          {value?.name ?? (
            <Input.Placeholder>{isNil(partCatalog) ? 'No catalog selected.' : placeholder}</Input.Placeholder>
          )}
        </InputBase>
      }
    >
      {isNotNil(partCatalog) && (
        <CategoryFields
          partCatalog={partCatalog}
          value={value}
          disableCategories={disableCategories}
          onChange={onChange}
        />
      )}
    </FormPopover>
  );
};
